import { Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { FormAutoCompleteInputDisplay } from '../../../components/presentation-components/form-field-components/new-and-edit/form-autocomplete-input';
import { FormSelectInputDisplay } from '../../../components/presentation-components/form-field-components/new-and-edit/form-select-input';
import { FormCheckBoxInputDisplay } from '../../../components/presentation-components/form-field-components/new-and-edit/form-checkbox-input';
import { ShallowFormDisplay } from '../../../components/presentation-components/form-field-components/general/shallow-form-display';
import ContactSupplierService from '../../../shared/services/contact-supplier/contact-supplier.service';
import CategoryService from '../../../shared/services/category/category.service';
import { ICategory } from '../../../shared/services/category/category.model';
import { getCategoryDisplayName } from '../../../shared/utils/category-utils';
import CloudEmissionsService from '../shared-services/cloud-emissions/cloud-emissions.service';
import {
  CLOUD_PROVIDER_COPY,
  CloudInvoiceMatching,
  ICloudConnectionStatus,
  ICloudScope,
  scopeNoun,
} from '../shared-services/cloud-emissions/cloud-emissions.model';

const cardSx = { borderColor: 'lightGrey.main', borderRadius: '10px', boxShadow: 'none' };

interface SupplierOption { id: string, name: string }

const MATCHING_OPTIONS: { value: CloudInvoiceMatching, label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly invoices, billed after the month (most customers)' },
  { value: 'MANUAL', label: 'Prepaid, committed spend or Marketplace billing: I will match by hand' },
];

const OFFSET_OPTIONS = [
  { value: 0, label: 'In the same month as the usage' },
  { value: 1, label: 'The month after the usage (usual)' },
  { value: 2, label: 'Two months after the usage' },
  { value: 3, label: 'Three months after the usage' },
];

/**
 * The connection's setup. Nothing is booked until an admin confirms it, because the supplier
 * mapping is what lets a vendor month replace its invoice estimate instead of being counted
 * beside it. Suggestions pre-fill the form (strong name matches ticked, weak ones offered) and
 * what was offered is sent with the confirmation, for the suggestion-outcome telemetry.
 */
export const CloudSetupCard: FunctionComponent<{
  status: ICloudConnectionStatus,
  isAdmin: boolean,
  onUpdated: (status: ICloudConnectionStatus) => void,
}> = ({ status, isAdmin, onUpdated }) => {
  const cloudEmissionsService = new CloudEmissionsService();
  const contactSupplierService = new ContactSupplierService();
  const categoryService = new CategoryService();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const settings = status.connection?.settings;
  const confirmed = !!settings?.supplierMappingConfirmedAt;
  const suggestedSuppliers = status.suggestions.suppliers;

  const [allSuppliers, setAllSuppliers] = useState<SupplierOption[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [scopes, setScopes] = useState<ICloudScope[]>(status.connection?.scopes ?? []);
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [bundled, setBundled] = useState<Record<string, boolean>>({});
  const [invoiceMatching, setInvoiceMatching] = useState<CloudInvoiceMatching>(settings?.invoiceMatching ?? 'MONTHLY');
  const [billingOffsetMonths, setBillingOffsetMonths] = useState<number>(settings?.billingOffsetMonths ?? 1);
  const [defaultCategoryId, setDefaultCategoryId] = useState<string | null>(settings?.defaultCategoryId ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Seed from the saved setup, or from the suggestions when nothing is saved yet.
  useEffect(() => {
    const saved = settings?.supplierIds ?? [];
    if (saved.length > 0 || confirmed) {
      setSupplierIds(saved.map(s => s.id));
      setBundled(Object.fromEntries(saved.map(s => [s.id, s.bundled])));
    } else {
      setSupplierIds(suggestedSuppliers.filter(s => s.strength === 'strong').map(s => s.id));
      setBundled({});
    }
    setDefaultCategoryId(settings?.defaultCategoryId ?? status.suggestions.defaultCategoryId ?? null);
    setScopes(status.connection?.scopes ?? []);
    setInvoiceMatching(settings?.invoiceMatching ?? 'MONTHLY');
    setBillingOffsetMonths(settings?.billingOffsetMonths ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    contactSupplierService.getActiveSuppliersLite()
      .then(rows => setAllSuppliers((rows || []).map(row => ({ id: row.id, name: row.name })).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setAllSuppliers([]));
    categoryService.getAllCategories()
      .then(rows => setCategories([...(rows || [])].sort((a, b) => getCategoryDisplayName(a).localeCompare(getCategoryDisplayName(b)))))
      .catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suppliers the organisation no longer lists as active still show by name.
  const supplierOptions = useMemo(() => {
    const byId = new Map(allSuppliers.map(s => [s.id, s]));
    for (const mapped of status.connection?.mappedSuppliers ?? []) {
      if (!byId.has(mapped.id)) byId.set(mapped.id, { id: mapped.id, name: mapped.name || 'Unnamed supplier' });
    }
    for (const suggestion of suggestedSuppliers) {
      if (!byId.has(suggestion.id)) byId.set(suggestion.id, { id: suggestion.id, name: suggestion.name });
    }
    return [...byId.values()];
  }, [allSuppliers, status, suggestedSuppliers]);

  const selectedSuppliers = supplierOptions.filter(option => supplierIds.includes(option.id));
  const selectedCategory = categories.find(category => category.id === defaultCategoryId) ?? null;
  const weakSuggestions = suggestedSuppliers.filter(s => s.strength === 'weak' && !supplierIds.includes(s.id));
  const scopeWord = scopeNoun(status.scopeLabel, { plural: true });

  const save = (confirmSetup: boolean) => {
    setIsSaving(true);
    cloudEmissionsService.updateSettings(status.provider, {
      scopes: scopes.map(scope => ({ id: scope.id, included: scope.included })),
      supplierIds: supplierIds.map(id => ({ id, bundled: !!bundled[id] })),
      invoiceMatching,
      billingOffsetMonths,
      defaultCategoryId,
      confirmSetup,
      suggestion: confirmSetup ? {
        suppliers: suggestedSuppliers.map(s => ({ id: s.id, strength: s.strength })),
        defaultCategoryId: status.suggestions.defaultCategoryId,
      } : undefined,
    })
      .then(updated => {
        userNotificationsContext.openSnackbar({
          open: true,
          message: confirmSetup ? 'Setup confirmed. Months are now matched to your invoices.' : 'Settings saved',
          backgroundColor: 'primary.dark',
        });
        onUpdated(updated);
      })
      .catch(err => userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The settings could not be saved. Please try again.',
        backgroundColor: 'text.primary',
      }))
      .finally(() => setIsSaving(false));
  };

  const refreshScopes = () => {
    setIsRefreshing(true);
    cloudEmissionsService.refreshScopes(status.provider)
      .then(onUpdated)
      .catch(err => userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The list could not be refreshed.',
        backgroundColor: 'text.primary',
      }))
      .finally(() => setIsRefreshing(false));
  };

  const readOnly = !isAdmin || isSaving;
  const canConfirm = supplierIds.length > 0 && !!defaultCategoryId;

  return (
    <Card variant='outlined' sx={cardSx}>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap={2}>
            <Stack spacing={1}>
              <Typography variant='h6' sx={{ color: 'text.primary' }}>Setup</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                Tell us which suppliers your {status.vendorName} invoices arrive under in your accounts. A vendor
                figure replaces the spend estimate on those invoices; without this step a month could be counted
                twice, so nothing is booked until setup is confirmed. If you buy through a reseller, choose the reseller.
              </Typography>
            </Stack>
            <Chip
              label={confirmed ? 'Confirmed' : 'Not confirmed'}
              color={confirmed ? 'success' : 'warning'}
              variant='outlined'
            />
          </Stack>

          <Stack spacing={1}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>
                {scopeNoun(status.scopeLabel, { plural: true, capitalised: true })} to sync
              </Typography>
              {isAdmin && (
                <LoadingButton variant='text' loading={isRefreshing} startIcon={<RefreshOutlined fontSize='small' />} onClick={refreshScopes}>
                  <Typography sx={{ pl: 0.5 }}>Refresh list</Typography>
                </LoadingButton>
              )}
            </Stack>
            {scopes.length === 0 && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                No {scopeWord} found yet. Refresh the list once your access is in place.
              </Typography>
            )}
            {scopes.map((scope, index) => (
              <FormCheckBoxInputDisplay
                key={scope.id}
                isEditPage={false}
                allowEdit={false}
                disabled={readOnly}
                title={scope.name === scope.id ? scope.id : scope.name}
                name={`scope-${scope.id}`}
                label={scope.missing ? 'Include (no longer listed by the vendor)' : 'Include'}
                value={scope.included}
                isTopRow={index === 0}
                isBottomRow={index === scopes.length - 1}
                onChange={(e: any) => setScopes(prev => prev.map(item => item.id === scope.id ? { ...item, included: e.target.checked } : item))}
              />
            ))}
          </Stack>

          <Stack spacing={1}>
            <FormAutoCompleteInputDisplay
              isEditPage={false}
              allowEdit={false}
              disabled={readOnly}
              multiple
              isTopRow
              isBottomRow={selectedSuppliers.length === 0}
              title='Cloud suppliers in your accounts'
              name='cloud-suppliers'
              options={supplierOptions}
              optionLabelKey='name'
              value={selectedSuppliers}
              onChange={(_: any, value: SupplierOption[]) => setSupplierIds((value || []).map(option => option.id))}
              tooltip='The suppliers your cloud invoices are posted under. Only their invoice records can have their spend estimate replaced.'
              virtualized
            />
            {selectedSuppliers.map((supplier, index) => (
              <FormCheckBoxInputDisplay
                key={supplier.id}
                isEditPage={false}
                allowEdit={false}
                disabled={readOnly}
                topBorder={false}
                isBottomRow={index === selectedSuppliers.length - 1}
                title={supplier.name}
                name={`bundled-${supplier.id}`}
                label={`Also bills other services on the same invoice (for example ${CLOUD_PROVIDER_COPY[status.provider].bundledExample})`}
                value={!!bundled[supplier.id]}
                onChange={(e: any) => setBundled(prev => ({ ...prev, [supplier.id]: e.target.checked }))}
              />
            ))}
            {weakSuggestions.length > 0 && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Also found: {weakSuggestions.map(s => s.name).join(', ')}. These are not ticked for you, because{' '}
                {CLOUD_PROVIDER_COPY[status.provider].weakSuppliersNote}.
              </Typography>
            )}
          </Stack>

          <Stack>
            <FormSelectInputDisplay
              isEditPage={false}
              allowEdit={false}
              disabled={readOnly}
              isTopRow
              title='How you are invoiced'
              name='invoice-matching'
              value={invoiceMatching}
              array={MATCHING_OPTIONS}
              valueKey='value'
              labelKey='label'
              onChange={(e: any) => setInvoiceMatching(e.target.value)}
              tooltip='Monthly invoices are paired with each month automatically. With prepaid or committed spend the invoice date says nothing about the month, so every month waits for you.'
            />
            {invoiceMatching === 'MONTHLY' && (
              <FormSelectInputDisplay
                isEditPage={false}
                allowEdit={false}
                disabled={readOnly}
                title='When the invoice arrives'
                name='billing-offset'
                value={billingOffsetMonths}
                array={OFFSET_OPTIONS}
                valueKey='value'
                labelKey='label'
                onChange={(e: any) => setBillingOffsetMonths(Number(e.target.value))}
              />
            )}
            <FormAutoCompleteInputDisplay
              isEditPage={false}
              allowEdit={false}
              disabled={readOnly}
              title='Category for months without an invoice'
              name='default-category'
              options={categories}
              optionLabelKey='name'
              getOptionLabel={(option: ICategory) => getCategoryDisplayName(option)}
              value={selectedCategory}
              onChange={(_: any, value: ICategory | null) => setDefaultCategoryId(value ? value.id : null)}
              tooltip='A month paired with an invoice keeps that invoice category. This one is used when a month is booked on its own.'
            />
            <ShallowFormDisplay
              name='Reporting basis'
              value={status.basis === 'MARKET' ? 'Market-based (from your organisation settings)' : 'Location-based (from your organisation settings)'}
              isBottomRow
            />
          </Stack>

          {status.basisNote && (
            <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>{status.basisNote}</Typography>
          )}
          <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>{status.exclusionsNote}</Typography>

          {isAdmin && (
            <Stack direction='row' justifyContent='flex-end' spacing={2}>
              {confirmed ? (
                <LoadingButton variant='contained' loading={isSaving} disabled={!canConfirm} onClick={() => save(false)}>
                  <Typography>Save changes</Typography>
                </LoadingButton>
              ) : (
                <>
                  <Button variant='outlined' disabled={isSaving} onClick={() => save(false)}>
                    <Typography>Save for later</Typography>
                  </Button>
                  <LoadingButton variant='contained' loading={isSaving} disabled={!canConfirm} onClick={() => save(true)}>
                    <Typography>Confirm setup</Typography>
                  </LoadingButton>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
